// TelegramChatLog.jsx — Halaman log percakapan Telegram lengkap (admin/system only)
// Menampilkan: pertanyaan utuh, jawaban bot utuh, estimasi token, waktu respons.
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

const PAGE_SIZE = 20;

export default function TelegramChatLog() {
  const { t, lang }           = useLang();
  const [logs,    setLogs]    = useState([]);
  const [stats,   setStats]   = useState(null);
  const [total,   setTotal]   = useState(0);
  const [offset,  setOffset]  = useState(0);
  const [search,  setSearch]  = useState('');
  const [query,   setQuery]   = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // id row yang dibuka

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/telegram/chat-logs', {
        params: { limit: PAGE_SIZE, offset, search: query },
      });
      setLogs(res.data.rows || []);
      setStats(res.data.stats || null);
      setTotal(res.data.total || 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [offset, query]);

  useEffect(() => { load(); }, [load]);

  function doSearch() {
    setOffset(0);
    setQuery(search.trim());
  }

  function fmtTime(tval) {
    if (!tval) return '-';
    const d = new Date(tval.includes('Z') || tval.includes('T') ? tval : tval.replace(' ', 'T') + 'Z');
    return d.toLocaleString(lang === 'en' ? 'en-US' : 'id-ID', { timeZone: 'Asia/Jakarta', dateStyle: 'medium', timeStyle: 'short' });
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">

      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="text-3xl">💬</span>
        <div>
          <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">{t('tgLog.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('tgLog.subtitle')}</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label={t('tgLog.statTotal')} value={stats.total_chats || 0} />
          <StatCard label={t('tgLog.statTotalToken')} value={Number(stats.total_tokens || 0).toLocaleString('id-ID')} accent="text-blue-500" />
          <StatCard label={t('tgLog.statAvgToken')} value={Math.round(stats.avg_tokens || 0).toLocaleString('id-ID')} />
          <StatCard label={t('tgLog.statAvgResp')} value={`${((stats.avg_time || 0) / 1000).toFixed(1)}s`} />
        </div>
      )}

      {/* Search */}
      <div className="flex gap-2">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch()}
          placeholder={t('tgLog.searchPlaceholder')}
          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={doSearch}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium transition-colors"
        >
          {t('tgLog.searchBtn')}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm animate-pulse">{t('tgLog.loading')}</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">{t('tgLog.empty')}</div>
      ) : (
        <div className="space-y-3">
          {logs.map(row => (
            <div
              key={row.id}
              className={`bg-white dark:bg-gray-800 rounded-xl border p-4 shadow-sm transition-colors ${
                row.is_error ? 'border-red-200 dark:border-red-800' : 'border-gray-100 dark:border-gray-700'
              }`}
            >
              {/* Meta */}
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 font-medium">
                    tg:{row.username || t('tgLog.unknown')}
                  </span>
                  {row.data_types && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                      {row.data_types}
                    </span>
                  )}
                  {row.is_error ? (
                    <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">{t('tgLog.error')}</span>
                  ) : null}
                </div>
                <span className="text-xs text-gray-400">{fmtTime(row.created_at)}</span>
              </div>

              {/* Question */}
              <div className="mb-2">
                <div className="text-xs font-medium text-gray-400 mb-0.5">{t('tgLog.question')}</div>
                <div className="text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                  {row.question}
                </div>
              </div>

              {/* Answer */}
              <div>
                <div className="text-xs font-medium text-gray-400 mb-0.5">{t('tgLog.answer')}</div>
                <div className={`text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2 whitespace-pre-wrap ${
                  expanded === row.id ? '' : 'line-clamp-4'
                }`}>
                  {row.answer}
                </div>
                {row.answer && row.answer.length > 200 && (
                  <button
                    onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                    className="mt-1 text-xs text-blue-500 hover:underline"
                  >
                    {expanded === row.id ? t('tgLog.hide') : t('tgLog.showMore')}
                  </button>
                )}
              </div>

              {/* Token footer */}
              <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400">
                <span title="Prompt tokens">↑ {row.tokens_prompt}</span>
                <span title="Completion tokens">↓ {row.tokens_completion}</span>
                <span className="font-medium text-blue-500" title="Total tokens (estimasi)">Σ {row.tokens_total} tok~</span>
                {row.response_time > 0 && <span title="Waktu respons">⏱ {(row.response_time / 1000).toFixed(1)}s</span>}
                {row.model && <span className="ml-auto font-mono">{row.provider}/{row.model}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('tgLog.prev')}
          </button>
          <span className="text-sm text-gray-400">{t('tgLog.pageInfo', { page, totalPages, total })}</span>
          <button
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={page >= totalPages}
            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('tgLog.next')}
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-3 shadow-sm">
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      <div className={`text-lg font-semibold ${accent || 'text-gray-800 dark:text-gray-100'}`}>{value}</div>
    </div>
  );
}
